import React from 'react'
import { observer } from 'mobx-react'
import CheckoutViewModel, { BillingMethodViewModel } from '../CheckoutViewModel'
import { match } from 'utils/pattern-match-util'
import Icon from 'components/Icon'
import CardPaymentMethodForm from './CardPaymentMethodForm'
import { Elements } from '@stripe/react-stripe-js'
import cx from 'classnames'
import LoadingOverlay from 'components/LoadingOverlay'
import ErrorBlock from 'components/ErrorBlock'
import styles from './Payment.sass'

const PaymentHeader = () => {
  return (
    <div className={styles.header}>
      <div className={styles.headerTitle}>Complete your payment</div>
      <div className={styles.headerDescription}>
        Review order summary and enter payment details to proceed.
      </div>
    </div>
  )
}

interface PaymentMethodItemCreditProps {
  vm: BillingMethodViewModel
}

const PaymentMethodItemCredit = observer(function PaymentMethodItemCredit({
  vm,
}: PaymentMethodItemCreditProps) {
  return (
    <>
      <div className={styles.paymentMethodDescription}>
        <div className={styles.creditCardType}>{vm.title}</div>
      </div>
    </>
  )
})

interface PaymentMethodItemStripeProps {
  vm: BillingMethodViewModel
}

const PaymentMethodItemStripe = observer(function PaymentMethodItemCredit({
  vm,
}: PaymentMethodItemStripeProps) {
  return (
    <>
      <div className={styles.paymentMethodDescription}>
        <span className={styles.descriptionBold}>{vm.brand}</span>{' '}
        <span>ending in</span>{' '}
        <span className={styles.descriptionBold}>{vm.last4}</span>
      </div>
      <div className={styles.paymentMethodExpiration}>
        <span>Expires</span>{' '}
        <span className={styles.expires}>{vm.expires}</span> <span>•</span>{' '}
        <span className={styles.csvCheckPassed}>CSV check passed</span>
      </div>
    </>
  )
})

interface PaymentMethodItemProps {
  vm: BillingMethodViewModel
}

const PaymentMethodItem = observer(function PaymentMethodItem({
  vm,
}: PaymentMethodItemProps) {
  return (
    <div
      onClick={() => vm.toggleSelected()}
      className={cx(styles.paymentMethodItem, vm.selected && styles.selected)}
    >
      {vm.selected && (
        <div className={styles.selectedBadge}>
          <Icon size={12} name="tick" /> Selected
        </div>
      )}
      {match(vm.type, {
        Stripe: () => <PaymentMethodItemStripe vm={vm} />,
        Credit: () => <PaymentMethodItemCredit vm={vm} />,
        [match.DEFAULT]: () => <div>Unknown Payment Method</div>,
      })}
    </div>
  )
})

interface PaymentMethodListProps {
  vm: CheckoutViewModel
}

const PaymentMethodList = observer(function PaymentMethodList({
  vm,
}: PaymentMethodListProps) {
  return (
    vm.paymentMethods && (
      <div className={styles.methodsList}>
        {vm.paymentMethods.map((pm) => (
          <PaymentMethodItem key={pm.id} vm={pm} />
        ))}
      </div>
    )
  )
})

const CardPaymentDivider = () => {
  return (
    <div className={styles.cardPaymentDivider}>
      <div className={styles.sideDivider}></div>
      <div className={styles.orPayWithCard}>Or pay with card</div>
      <div className={styles.sideDivider}></div>
    </div>
  )
}

interface PaymentProps {
  vm: CheckoutViewModel
}

export default observer(function Payment({ vm }: PaymentProps) {
  return vm.fetchCheckoutInfo.match({
    pending: () => <LoadingOverlay />,
    resolved: () => (
      <div className={styles.root}>
        <div className={styles.payment}>
          <PaymentHeader />
          <PaymentMethodList vm={vm} />
          {vm.hasPaymentMethods && <CardPaymentDivider />}
          {vm.cardPaymentMethodFormVM && (
            <div className={styles.cardPaymentContainer}>
              <Elements stripe={vm.stripe!} options={{ locale: 'en' }}>
                <CardPaymentMethodForm vm={vm.cardPaymentMethodFormVM} />
              </Elements>
            </div>
          )}
        </div>
      </div>
    ),
    rejected: () => <ErrorBlock error={vm.fetchCheckoutInfo.error} />,
  })
})
